import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Menu, X } from "lucide-react";

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const links = [
    { href: "#how", label: "How it works" },
    { href: "#build", label: "What you'll build" },
    { href: "#offer", label: "Pricing" },
    { href: "#instructor", label: "Instructor" },
    { href: "#for-organizations", label: "For teams" },
    { href: "#faq", label: "FAQ" },
  ];

  const onHome = pathname === "/";

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-white/85 backdrop-blur-xl border-b border-[var(--line)]"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-[1240px] mx-auto px-6 lg:px-10 h-16 flex items-center justify-between">
        <Link to="/" data-testid="logo-home-link" className="flex items-center gap-2.5">
          <div className="w-7 h-7 rounded-full bg-ink grid place-items-center text-[10px] font-bold text-white">F</div>
          <span className="font-display text-ink text-[15px] tracking-tight font-bold">
            Freedom In Me
            <span className="text-[var(--muted-2)] font-normal mx-1.5">/</span>
            <span className="font-serif-em italic font-light text-brand">Build Lab</span>
          </span>
        </Link>

        {onHome && (
          <nav className="hidden md:flex items-center gap-9 text-sm text-[var(--muted)]">
            {links.map((l) => (
              <a
                key={l.href}
                href={l.href}
                data-testid={`nav-${l.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                className="hover:text-ink transition-colors"
              >
                {l.label}
              </a>
            ))}
          </nav>
        )}

        <div className="hidden md:flex items-center gap-3">
          <a href="#register" data-testid="header-register-cta">
            <Button className="btn-ink rounded-full h-10 px-5 text-sm font-medium">
              Save My Seat
            </Button>
          </a>
        </div>

        <button
          data-testid="mobile-menu-toggle"
          onClick={() => setOpen((v) => !v)}
          className="md:hidden text-ink p-2"
          aria-label="Toggle menu"
        >
          {open ? <X size={20} /> : <Menu size={20} />}
        </button>
      </div>

      {open && (
        <div className="md:hidden border-t border-[var(--line)] bg-white">
          <div className="px-6 py-5 flex flex-col gap-4">
            {onHome &&
              links.map((l) => (
                <a
                  key={l.href}
                  href={l.href}
                  onClick={() => setOpen(false)}
                  data-testid={`mobile-nav-${l.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                  className="text-ink text-base"
                >
                  {l.label}
                </a>
              ))}
            <a href="#register" onClick={() => setOpen(false)} data-testid="mobile-register-cta">
              <Button className="w-full btn-ink rounded-full h-12 font-medium">Save My Seat</Button>
            </a>
          </div>
        </div>
      )}
    </header>
  );
}
